import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useTheme } from '@/hooks/useTheme';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePageTitle } from '@/contexts/PageTitleContext';

/**
 * PageHeader Component
 *
 * Reusable page header with gradient background or custom header image
 * from branding configuration.
 *
 * Features:
 * - Displays title and optional subtitle
 * - Optional icon
 * - Uses page-specific header images from branding.assets
 * - Falls back to gradient with primary/secondary colors
 * - Responsive height and padding
 * - Sets browser document title via documentTitle prop
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="What are you creating today?"
 *   subtitle="Create and manage your social media content"
 *   icon={ICONS.MAGIC_WAND}
 *   headerType="list"
 *   documentTitle="Ägile Creator"
 * />
 * ```
 */

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: string;
  /** Render a custom icon node (e.g. the same SVG used in the rail menu).
   *  Takes precedence over `icon` when both are provided. */
  iconNode?: React.ReactNode;
  headerType?: 'dashboard' | 'calendar' | 'list' | 'brandcheck' | 'release' | 'agencycc' | 'approval';
  documentTitle?: string;
  /** Show a back button linking to this path */
  backTo?: string;
  /** Label for the back button (shown as text next to arrow when not minimized) */
  backLabel?: string;
}

export const PageHeader = ({ title, subtitle, icon, iconNode, headerType = 'dashboard', documentTitle, backTo, backLabel }: PageHeaderProps) => {
  const { branding } = useTheme();
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(false);
  // Tracks the last-committed minimized state synchronously so the scroll
  // handler can apply hysteresis without depending on React's (possibly
  // stale, batched) state value.
  const minimizedRef = useRef(false);
  const pageTitle = usePageTitle();

  // Set document title if provided
  useDocumentTitle(documentTitle || '');

  // Register this page's title + icon in the context so the navbar can display it
  // Prefer documentTitle (always a plain string), fall back to title if it's a string
  useEffect(() => {
    const titleStr = documentTitle || (typeof title === 'string' ? title : '');
    pageTitle.setPageTitle(titleStr, icon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, icon, documentTitle]);

  // Clean up context on unmount so stale title doesn't linger
  useEffect(() => {
    return () => {
      pageTitle.setPageTitle('');
      pageTitle.setMinimized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Minimize header on scroll down and sync minimized state to context.
  //
  // Two independent safeguards against scroll-jank "vibration" (rapid
  // expand/collapse toggling on pages that are only barely scrollable):
  //
  // 1. Layout invariance (primary fix): the outer wrapper below always
  //    reserves the *expanded* height in document flow; only the inner
  //    banner (absolutely positioned within it) visually shrinks. So
  //    toggling `minimized` never changes document.scrollHeight, and thus
  //    can never snap scrollY back across the threshold on its own.
  // 2. Hysteresis (defense-in-depth): collapse only once scrollY > 80,
  //    expand only once scrollY < 20. Using two distinct thresholds (instead
  //    of one edge) means even a stray sub-pixel/layout shift can't cause
  //    rapid re-triggering, since the state can't flip back until scrollY
  //    crosses the *other* threshold.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const prev = minimizedRef.current;
      const next = prev ? y >= 20 : y > 80;
      if (next !== prev) {
        minimizedRef.current = next;
        setMinimized(next);
        pageTitle.setMinimized(next);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map headerType to branding asset key
  const headerAssetMap: Record<string, string | undefined> = {
    dashboard: branding?.assets?.dashboardHeader,
    calendar: branding?.assets?.calendarHeader,
    list: branding?.assets?.listHeader,
    brandcheck: branding?.assets?.brandcheckHeader,
    release: branding?.assets?.releaseHeader,
    agencycc: branding?.assets?.agencyccHeader,
    approval: branding?.assets?.approvalHeader,
  };

  const headerImage = headerType ? headerAssetMap[headerType] : undefined;
  const hasHeaderImage = !!headerImage;

  return (
    // Reserved-space wrapper: height is ALWAYS the expanded size, regardless
    // of `minimized`. This is what makes document.scrollHeight invariant to
    // the collapse state — the banner below shrinks visually inside this
    // fixed-height box, so collapsing can never shrink the document and snap
    // scrollY back down (the root cause of the old vibration bug).
    <div className="relative -mx-6 -mt-6 mb-2 sm:mb-3 h-32 sm:h-48 md:h-56">
      <div
        className={`absolute inset-x-0 top-0 overflow-hidden transition-all duration-300 ease-in-out ${minimized ? 'h-12 sm:h-16 lg:h-20' : 'h-32 sm:h-48 md:h-56'}`}
        style={
          hasHeaderImage
            ? {
                backgroundImage: `url(${headerImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        {/* Gradient fallback when no header image */}
        {!hasHeaderImage && (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600" />
        )}

        {/* Dark overlay for header images - improves text contrast */}
        {hasHeaderImage && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/20" />
        )}

        {/* Back button */}
        {backTo && (
          <button
            onClick={() => navigate(backTo)}
            className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-all text-sm font-medium"
          >
            <Icon name={ICONS.ARROW_LEFT} size="xs" ariaHidden />
            {!minimized && backLabel && <span className="hidden sm:inline">{backLabel}</span>}
          </button>
        )}

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            {iconNode ? (
              <span
                className={`flex items-center justify-center text-white shrink-0 ${
                  minimized ? '[&>svg]:w-5 [&>svg]:h-5' : '[&>svg]:w-9 [&>svg]:h-9 sm:[&>svg]:w-12 sm:[&>svg]:h-12'
                }`}
                aria-hidden
              >
                {iconNode}
              </span>
            ) : icon ? (
              <Icon
                name={icon}
                size={minimized ? 'xs' : 'md'}
                color="text-white"
                ariaHidden
                // Mobile keeps the md baseline. Desktop bumps up to match the
                // title's responsive scaling so thin-stroke glyphs (e.g. hm-board)
                // read at the same visual weight as chunkier ones.
                className="sm:!text-4xl md:!text-5xl"
              />
            ) : null}
            <h1 className={`font-wagner font-bold text-white drop-shadow-lg transition-all duration-300 leading-tight ${minimized ? 'text-sm sm:text-lg lg:text-xl' : 'text-xl sm:text-4xl md:text-5xl'}`}>{title}</h1>
          </div>
          {subtitle && (
            <p className={`text-white/90 drop-shadow-md transition-all duration-300 ${minimized ? 'opacity-0 h-0 mt-0 overflow-hidden' : 'opacity-100 mt-1 sm:mt-2 text-sm sm:text-base md:text-lg'}`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
